import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Message,
  ConversationTypes,
  WebRTCError,
  ValidLanguages,
} from "../../../../pages/symptomCollector/utils/types";
import { useNavigate, useLocation } from "react-router-dom";

import bubble from "./../../../../assets/images/bubble.svg";
import DDxInputSelectorBg from "./../../../../assets/images/ddx-input-selector-bg.webp";
import chatTypingIdicator from "./../../../../assets/animations/typing.gif";
import speakLoading from "./../../../../assets/animations/speak-loading.gif";
import chat from "./../../../../assets/images/aa-close.svg";
import muteMic from "./../../../../assets/images/mute-mic.svg";
import chevron from "./../../../../assets/images/chevron-purple.svg";
import voiceBottomBg from "./../../../../assets/images/aa-bottom-bg.svg";
import {
  LangSpecificMsg,
  LANGUAGE,
  languageOptions,
  OPENAI_SUPPORTED_LANGUAGES,
} from "../../../../pages/symptomCollector/utils/constants";
import { Button } from "antd";
import {
  checkAndRequestPermissions,
  generateUUID,
  initializeAudioContext,
  isConnectionActive,
  isIOS,
} from "../../../../pages/symptomCollector/symptomCollectorHelper";
import SelectSpecialityDrawer from "../selectSpecialityDrawer/SelectSpecialityDrawer";
import userIcon from "../../../../assets/images/user-purple.svg";
import calendarIcon from "../../../../assets/images/calendar.svg";
import close from "../../../../assets/images/close-outline.svg";
import activeMic from "../../../../assets/images/aa-mic.svg";
import { ReactComponent as Edit } from "../../../../assets/images/ddx-edit.svg";
import {
  addNewPatient,
  bookAppointment,
  cancelAppointment,
  establishWebRtcConnection,
  getAppointmentSlots,
  getDoctors,
  getDoctorsByName,
  getPatientDetails,
  rescheduleAppointment,
  getSpecialities,
  startAzureOpenAISession,
  getInstructions,
  saveConversationData,
  appointmentSlotsValidator,
  editPatient,
  getAppointmentSlotsForVoice,
} from "../../service";
import {
  APPOINTMENT_AGENT_WEBSOCKET_URL,
  SYMPTOM_COLLECTOR_TRANSCRIPTION_MODEL,
} from "../../../../envConstants";
import SelectSlotDrawer from "./SelectSlotDrawer";
import { calculateAge, trackEvent } from "../../../../utils/helper";
import { getLocalStorage } from "../../../../utils/localStorage";
import GenderInput from "../patientDetails/GenderInput";
import AgeInput from "../patientDetails/AgeInput";
import dayjs from "dayjs";
import Layout from "../../../../commonComponents/Layout";
import ConfirmationPopup from "../confirmationPopup/ConfirmationPopup";
import AnimatedMessage from "./AnimatedMessage";
import "./SelectSlot.css";
import { ConfirmSlotDrawer } from "./ConfirmSlotDrawer";
import VoiceWaveVisualizer from "./WaveVisualizer";
import VoiceBubble from "./VoiceBubble";

// Add shimmer animation styles
const shimmerStyles = `
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 2s infinite;
}
`;

// Inject styles into the document
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = shimmerStyles;
  document.head.appendChild(styleSheet);
}

const BookAppointment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { state } = location || {};

  const isSafari = useMemo(
    () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
    []
  );
  const isIOSDevice = useMemo(
    () => /iPad|iPhone|iPod/.test(navigator.userAgent),
    []
  );

  const {
    avatarImage,
    agentDetails,
    type = "chat",
    pam_id,
    conversationId,
    language,
  } = state || {};

  const selectedLanguage = language || getLocalStorage(LANGUAGE);

  const LangSpecificMsgData =
    LangSpecificMsg[(selectedLanguage as ValidLanguages) || "english"];

  // State with types

  const [showSelectSpecialityDrawer, setShowSelectSpecialityDrawer] =
    useState<any>(false);
  const [showExitPopup, setShowExitPopup] = useState<boolean>(false);
  const [specialitiesList, setSpecialitiesList] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState<any>([]);
  const [doctosList, setDoctorsList] = useState([]);

  const [patientList, setPatientList] = useState<any>([]);
  const [showSelectSlot, setShowSelectSlot] = useState<boolean>(false);
  const [gender, setGender] = useState<"M" | "F" | null>(null);
  const [showGender, setShowGender] = useState<boolean>(false);
  const [age, setAge] = useState<any | null>(null);
  const [showAge, setShowAge] = useState<boolean>(false);

  const [messages, setMessages] = useState<Message[]>(
    type === "voice" ? [] : state?.messages || []
  );
  const initialMessageCountRef = useRef<number>(
    type === "voice"
      ? 0
      : Array.isArray(state?.messages)
      ? state?.messages.length
      : 0
  );
  const [value, setValue] = useState<string>("");
  const [isTextButtonClicked, setIsTextButtonClicked] =
    useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isVoiceSelected, setIsVoiceSelected] = useState<boolean>(
    type === "voice"
  );
  const [options, setOptions] = useState<any>([]);
  const [isEnd, setIsEnd] = useState<boolean>(false);
  const [transcribedParagraph, setTranscribedParagraph] = useState("");
  const [peerConnection, setPeerConnection] =
    useState<RTCPeerConnection | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [soundLevel, setSoundLevel] = useState(1);
  const [conversationChunks, setConversationChunks] = useState<Blob[]>([]);
  const [mergedRecorder, setMergedRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState<any>(null);
  const [instructions, setInstructions] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isAudioDetected, setIsAudioDetected] = useState(false);
  const [audioIntensity, setAudioIntensity] = useState(0);

  // Separate refs for audio level detection
  const audioLevelContextRef = useRef<AudioContext | null>(null);
  const audioLevelAnalyserRef = useRef<AnalyserNode | null>(null);
  const audioLevelFrameRef = useRef<number | null>(null);
  const audioLevelStreamRef = useRef<MediaStream | null>(null);
  const audioLevelHistoryRef = useRef<number[]>([]); // For moving average
  const audioDetectionStartTimeRef = useRef<number | null>(null); // Track when audio detection started

  // Refs with types
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const lastTranscriptRef = useRef<string>("");
  const userTranscriptionRef = useRef<ConversationTypes[]>([]);
  const agentTranscriptionRef = useRef<ConversationTypes[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameRef = useRef<number>();
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const currentIncrementalTextRef = useRef<string>("");

  const audioBlobsRef = useRef<Blob[]>([]); // New ref for storing audio blobs

  const messageContainerRef = useRef<HTMLDivElement>(null);
  const textMessagesContainerRef = useRef<HTMLDivElement>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const patientsDetailsRef = useRef<any>([]);
  const doctorsListRef = useRef<any>([]);

  const patientGender =
    appointmentDetails?.pm_gender ||
    appointmentDetails?.gender ||
    appointmentDetails?.patients_gender;

  const patientAge = useMemo(() => {
    return calculateAge(
      appointmentDetails?.pm_dob ||
        appointmentDetails?.date_of_birth ||
        appointmentDetails?.patients_dob
    );
  }, [appointmentDetails]);

  const moengageData = {
    "Login Mobile No": agentDetails?.patient_contact_no,
    "Clinic Id": agentDetails?.hm_id,
    "Clinic Name": agentDetails?.hospital_name,
    "Time Stamp": dayjs().format("YYYY-MM-DD HH:mm:ss"),
  };

  useEffect(() => {
    // Only scroll to bottom if there are messages, not on initial load
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, options, isEnd, isTextButtonClicked]); // Scroll whenever messages update

  useEffect(() => {
    if (selectedLanguage && !instructions && isVoiceSelected) {
      fetchInstructions();
    }
  }, [selectedLanguage, isVoiceSelected]);

  const showSkeletonLoaderData = useMemo(() => {
    return (
      !isVoiceSelected &&
      !isEnd &&
      options?.length === 0 &&
      (messages?.length === 0 ||
        messages?.[messages.length - 1]?.type === "user")
    );
  }, [isVoiceSelected, messages, isEnd, options]);

  // Make sure to update streamRef when mediaStream changes
  useEffect(() => {
    if (mediaStream) {
      streamRef.current = mediaStream;
    }
  }, [mediaStream]);

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [messages]); // Scroll whenever messages update

  useEffect(() => {
    if (pam_id && state) {
      setDoctorsList(state.doctosList);
      setPatientList(state.patientList);
      setSpecialitiesList(state.specialitiesList);
      patientsDetailsRef.current = state.patientsDetailsRef;
      doctorsListRef.current = state.doctorsListRef;

      const selectedDoctor = state.doctorsListRef?.find(
        (item: any) => String(item.um_id) === String(state.um_id)
      );
      setSelectedDoctor(selectedDoctor);
    }
  }, [pam_id]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!isVoiceSelected && !isEnd) {
      const startWebSocket = () => {
        socketRef.current = new WebSocket(
          APPOINTMENT_AGENT_WEBSOCKET_URL || ""
        );

        socketRef.current.onopen = () => {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            const chat_history = messages.map((item) => ({
              role: item.type,
              content: item.text.endsWith("\n") ? item.text : item.text + "\n",
            }));
            const {
              um_id,
              patient_unique_id,
              pm_pid,
              appointment_date,
              date,
              appointment_start_time,
              appointment_end_time,
              appointment_duration,
              pam_id,
              summary,
              pm_gender,
              pm_first_name,
              pm_last_name,
              pm_dob,
            } = state;
            socketRef.current?.send(
              JSON.stringify({
                language: selectedLanguage,
                hm_id: agentDetails?.hm_id,
                mobile_number: agentDetails?.patient_contact_no,
                hospital_name: agentDetails?.hospital_name,
                agent_gender: agentDetails?.agent_gender,
                agent_name: agentDetails?.agent_name,
                hm_business_id: agentDetails?.hm_business_id,
                chat_history: summary || chat_history || "",
                um_id: um_id,
                patient_unique_id: patient_unique_id,
                pm_pid: pm_pid,
                appointment_date: appointment_date || date,
                appointment_start_time: appointment_start_time,
                appointment_end_time: appointment_end_time,
                appointment_duration: appointment_duration,
                pam_id: pam_id,
                pm_gender: pm_gender,
                pm_first_name: pm_first_name,
                pm_last_name: pm_last_name,
                pm_dob: pm_dob,
              })
            );
            socketRef.current?.send(
              JSON.stringify({
                type: "text",
                text: pam_id ? "reschedule" : "Hi",
                language: selectedLanguage,
              })
            );
          }
        };

        socketRef.current.onmessage = async (event: any) => {
          try {
            const msg = JSON.parse(event.data);
            console.log("msg", msg);
            if (msg.type === "response.text.done") {
              setIsChatLoading(false);

              if (msg?.data?.key === "speciality") {
                const specialitiesData = msg?.data?.value?.map((item: any) => ({
                  name: item,
                  key: "specialities",
                }));
                setSpecialitiesList(specialitiesData);
                setOptions(specialitiesData);
              } else if (msg?.data?.key === "doctors") {
                const doctorsData = msg?.data?.value?.map((item: any) => ({
                  name: item.um_name,
                  key: "doctors",
                }));
                doctorsListRef.current = msg?.data?.value;
                setDoctorsList(doctorsData);
                setOptions(doctorsData);
              } else if (msg?.data?.key === "patient_list") {
                const patientsData = msg?.data?.value?.map((item: any) => ({
                  name: `${item.pm_first_name} (${
                    item.pm_gender === "Female" ? "F" : "M"
                  }, ${item.pm_dob ? calculateAge(item.pm_dob)?.years : ""})`,
                  key: "patient_list",
                }));
                patientsDetailsRef.current = msg?.data?.value;
                setPatientList(patientsData);
                setOptions([
                  ...patientsData,
                  {
                    name: "new patient",
                    key: "patient_list",
                  },
                ]);
              }

              setMessages((prev) => [
                ...prev,
                {
                  type: "agent",
                  text: msg.data.transcript,
                  timestamp: Date.now(),
                },
              ]);
              agentTranscriptionRef.current = [
                ...agentTranscriptionRef.current,
                {
                  data: msg.data.transcript,
                  mode: "text",
                },
              ];
              setTranscribedParagraph(
                (prev) => `${prev}\nAgent:\n${msg.data.transcript}\n`
              );
            }
            if (msg?.key === "new_patient") {
              setShowGender(true);
            }

            if (
              msg.type === "conversation.input.text.done" &&
              msg.data?.trim() !== "Hi"
            ) {
              setMessages((prev) => [
                ...prev,
                {
                  type: "user",
                  text: msg.data.transcript,
                  timestamp: Date.now(),
                },
              ]);
              userTranscriptionRef.current = [
                ...userTranscriptionRef.current,
                {
                  data: msg.data.transcript,
                  mode: "text",
                },
              ];
              setTranscribedParagraph(
                (prev) => `${prev}\nUser:\n${msg.data.transcript}`
              );
            }

            if (msg.type === "book_appointment_payload") {
              const selectedPatient = patientsDetailsRef?.current?.find(
                (item: any) => item?.pm_pid === msg.arguments?.pm_pid
              );
              const doctorDetails = doctorsListRef?.current?.find(
                (item: any) => item?.um_id == msg.arguments?.um_id
              );
              setAppointmentDetails({
                ...msg.arguments,
                ...selectedPatient,
                hospital_business_id: agentDetails?.hm_business_id,
                doctor_name: doctorDetails?.um_name,
              });
              setIsEnd(true);
            }
            if (msg?.type === "get_appointment_slots_payload") {
              const slotsResponse: any = await getAppointmentSlotsForVoice({
                um_id: msg?.arguments?.um_id,
                hm_id: msg?.arguments?.hm_id,
                hm_business_id: msg?.arguments?.hm_business_id,
                appointment_date: dayjs().format("YYYY-MM-DD"),
              });
              const recentSlots = slotsResponse.slice(0, 2);
              setOptions([
                ...recentSlots.map((slot: any) => ({
                  name: slot.start,
                  key: "slots",
                })),
                { name: "List all available slots", key: "slots" },
              ]);
              setSelectedDoctor(msg.arguments);
            }
          } catch (error) {
            console.error("Error processing message:", error);
          }
        };
      };

      startWebSocket();
      return () => socketRef.current?.close();
    }
  }, [selectedLanguage, isVoiceSelected]);

  useEffect(() => {
    if (isVoiceSelected && instructions) {
      startListening();
      cleanup();
    }
  }, [isVoiceSelected, instructions]);

  const fetchInstructions = async () => {
    const chat_history = messages.map((item) => ({
      role: item.type,
      content: item.text.endsWith("\n") ? item.text : item.text + "\n",
    }));

    const instructionsRes = await getInstructions(
      selectedLanguage,
      JSON.stringify(chat_history),
      state,
      agentDetails?.hospitalType,
      doctorsListRef,
      selectedDoctor
    );
    setInstructions(instructionsRes);
  };

  const configureData = (channel: RTCDataChannel) => {
    const selectedLang = languageOptions.find(
      (lang) => lang.value === selectedLanguage
    );
    const event = {
      type: "session.update",
      session: {
        modalities: ["text", "audio"],
        input_audio_noise_reduction: {
          type: "far_field",
        },
        turn_detection: {
          type: "server_vad",
          threshold: 0.75, // Increased to 0.75 for better noise filtering
          prefix_padding_ms: 300,
          silence_duration_ms: 1200, // Increased to 1200ms to ensure user finished speaking
          create_response: true,
        },
        input_audio_transcription: {
          model: SYMPTOM_COLLECTOR_TRANSCRIPTION_MODEL,
          ...(OPENAI_SUPPORTED_LANGUAGES.includes(
            selectedLang?.languageCode || ""
          ) && {
            language: selectedLang?.languageCode || "en",
          }),
        },
      },
    };
    channel.send(JSON.stringify(event));
  };

  // Clean audio level detection (separate from voice chat)
  const startAudioLevelDetection = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioLevelStreamRef.current = stream;

      const audioContext = new AudioContext();
      audioLevelContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048; // Increased for better frequency resolution
      analyser.smoothingTimeConstant = 0.8; // Increased smoothing to reduce noise spikes
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      audioLevelAnalyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const sampleRate = audioContext.sampleRate;
      const frequencyBinWidth = sampleRate / (analyser.fftSize * 2);

      // Speech frequency range: 300-3400 Hz (typical human speech range)
      const getSpeechFrequencyRange = () => {
        const speechMinHz = 300;
        const speechMaxHz = 3400;
        const minBin = Math.floor(speechMinHz / frequencyBinWidth);
        const maxBin = Math.floor(speechMaxHz / frequencyBinWidth);
        return { minBin, maxBin };
      };

      const { minBin, maxBin } = getSpeechFrequencyRange();
      const MIN_DETECTION_DURATION_MS = 200; // Audio must be detected for at least 200ms
      const MOVING_AVERAGE_SIZE = 10; // Number of samples for moving average

      const updateAudioLevel = () => {
        if (!audioLevelAnalyserRef.current) return;

        audioLevelAnalyserRef.current.getByteFrequencyData(dataArray);

        // Focus on speech frequency range (300-3400 Hz) to filter out noise
        const speechFrequencies = dataArray.slice(minBin, maxBin);
        
        // Use RMS (Root Mean Square) for better detection of actual audio vs noise
        const sumSquares = speechFrequencies.reduce((sum, value) => sum + value * value, 0);
        const rms = Math.sqrt(sumSquares / speechFrequencies.length);
        
        // Normalize RMS to 0-10 range
        const normalizedLevel = Math.min(10, (rms / 255) * 40);

        if (!isMuted) {
          // Add to moving average history
          audioLevelHistoryRef.current.push(normalizedLevel);
          if (audioLevelHistoryRef.current.length > MOVING_AVERAGE_SIZE) {
            audioLevelHistoryRef.current.shift();
          }

          // Calculate moving average to smooth out spikes
          const movingAverage = audioLevelHistoryRef.current.reduce((a, b) => a + b, 0) / audioLevelHistoryRef.current.length;
          
          setAudioIntensity(movingAverage);

          // Only detect audio when:
          // 1. Moving average is above threshold (reduces noise spikes)
          // 2. Current level is also above threshold (ensures sustained audio)
          const isAboveThreshold = movingAverage > 4.5 && normalizedLevel > 4;

          if (isAboveThreshold) {
            // Track when audio detection started
            if (audioDetectionStartTimeRef.current === null) {
              audioDetectionStartTimeRef.current = Date.now();
            }

            // Only set as detected if audio has been above threshold for minimum duration
            const detectionDuration = Date.now() - (audioDetectionStartTimeRef.current || 0);
            if (detectionDuration >= MIN_DETECTION_DURATION_MS) {
              setIsAudioDetected(true);
            }
          } else {
            // Reset detection timer if audio drops below threshold
            audioDetectionStartTimeRef.current = null;
            setIsAudioDetected(false);
          }
        } else {
          setAudioIntensity(0);
          setIsAudioDetected(false);
          audioDetectionStartTimeRef.current = null;
          audioLevelHistoryRef.current = [];
        }

        audioLevelFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };

      updateAudioLevel();
    } catch (err) {
      console.error("Error accessing microphone for audio level:", err);
    }
  }, [isMuted]);

  const stopAudioLevelDetection = useCallback(async () => {
    if (audioLevelFrameRef.current) {
      cancelAnimationFrame(audioLevelFrameRef.current);
      audioLevelFrameRef.current = null;
    }

    if (audioLevelStreamRef.current) {
      audioLevelStreamRef.current.getTracks().forEach((track) => track.stop());
      audioLevelStreamRef.current = null;
    }

    if (
      audioLevelContextRef.current &&
      audioLevelContextRef.current.state !== "closed"
    ) {
      try {
        await audioLevelContextRef.current.close();
      } catch (err) {
        console.error("Error closing audio level AudioContext:", err);
      }
      audioLevelContextRef.current = null;
    }

    // Reset detection state
    audioDetectionStartTimeRef.current = null;
    audioLevelHistoryRef.current = [];
    setAudioIntensity(0);
    setIsAudioDetected(false);
  }, []);

  // Legacy monitorAudioLevel for compatibility
  const monitorAudioLevel = useCallback(() => {
    // Keep the original logic for soundLevel but don't interfere with audioIntensity
    if (!analyserRef.current || !mediaStream) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      if (isMuted) {
        setSoundLevel(1);
        return;
      }

      try {
        analyser.getByteFrequencyData(dataArray);

        let maxValue = 0;
        for (let i = 0; i < dataArray.length; i++) {
          if (dataArray[i] > maxValue) {
            maxValue = dataArray[i];
          }
        }

        let newLevel = 1;
        if (maxValue > 0) {
          newLevel = Math.max(1, Math.min(10, 1 + Math.floor(maxValue / 15)));
        }

        setSoundLevel((prev) => {
          const delta = newLevel - prev;
          return prev + delta * 0.5;
        });

        if (!isMuted) {
          animationFrameRef.current = requestAnimationFrame(updateLevel);
        }
      } catch (error) {
        console.error("Error in updateLevel:", error);
      }
    };

    updateLevel();
  }, [isMuted, mediaStream]);

  // Start/stop audio level detection for wobbling effect
  useEffect(() => {
    if (isVoiceSelected) {
      startAudioLevelDetection();
    } else {
      stopAudioLevelDetection();
    }

    return () => {
      stopAudioLevelDetection();
    };
  }, [isVoiceSelected, startAudioLevelDetection, stopAudioLevelDetection]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clean up all audio contexts and resources
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close().catch(console.error);
      }
      if (
        audioLevelContextRef.current &&
        audioLevelContextRef.current.state !== "closed"
      ) {
        audioLevelContextRef.current.close().catch(console.error);
      }
      if (audioLevelFrameRef.current) {
        cancelAnimationFrame(audioLevelFrameRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        socketRef.current.close();
      }
    };
  }, []);

  const setupAudioAnalysis = useCallback(
    async (stream: MediaStream) => {
      try {
        // Cleanup existing
        if (
          audioContextRef.current &&
          audioContextRef.current.state !== "closed"
        ) {
          try {
            await audioContextRef.current.close();
          } catch (err) {
            console.error("Error closing AudioContext:", err);
          }
          audioContextRef.current = null;
        }
        if (sourceRef.current) {
          sourceRef.current.disconnect();
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.1;
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        sourceRef.current = source;

        if (!isMuted) {
          monitorAudioLevel();
        } else {
          setSoundLevel(1);
        }
      } catch (error) {
        console.error("Error in setupAudioAnalysis:", error);
      }
    },
    [isMuted, monitorAudioLevel]
  );

  const startListening = async () => {
    if (isConnectionActive(peerConnectionRef.current)) {
      // Connection already active, reusing existing connection
      return;
    }
    if (!peerConnectionRef.current) {
      setIsLoading(true);
      try {
        // Check permissions first
        const hasPermission = await checkAndRequestPermissions();
        if (!hasPermission) {
          throw new Error("Microphone permission not granted");
        }

        // Create RTCPeerConnection with explicit configuration for Safari
        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:stun1.l.google.com:19302" },
          ],
        });

        // Add connection state logging with more detail
        pc.oniceconnectionstatechange = () => {
          if (pc.iceConnectionState === "failed") {
            stopListening();
          }
        };

        setPeerConnection(pc);

        // Try to get audio stream with more detailed error handling
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: { ideal: true },
              noiseSuppression: { ideal: true },
              autoGainControl: { ideal: true },
              sampleRate: { ideal: 44100 },
            },
          });
        } catch (mediaError: any) {
          console.error(
            "MediaDevices error:",
            mediaError.name,
            mediaError.message
          );
          throw new Error(`Failed to access microphone: ${mediaError.message}`);
        }

        setMediaStream(stream);

        // Initialize audio context first
        const audioContext = await initializeAudioContext();

        try {
          // Create audio processing pipeline
          const userSource = audioContext.createMediaStreamSource(stream);
          const destination = audioContext.createMediaStreamDestination();

          // Add a gain node to control volume
          const gainNode = audioContext.createGain();
          gainNode.gain.value = 1.0; // Normal volume

          // Connect the audio nodes
          userSource.connect(gainNode).connect(destination);

          // Add tracks to peer connection
          stream.getTracks().forEach((track) => {
            pc.addTransceiver(track, {
              direction: "sendrecv",
              streams: [stream],
            });
          });

          pc.ontrack = (event) => {
            if (event.track.kind === "audio") {
              const agentAudioStream = event.streams[0];
              const agentAudio = document.createElement("audio");
              agentAudio.srcObject = agentAudioStream;
              agentAudio.autoplay = true;
              document.body.appendChild(agentAudio);

              const agentSource =
                audioContext.createMediaStreamSource(agentAudioStream);
              const agentGain = audioContext.createGain();
              agentGain.gain.value = 1.0;

              agentSource.connect(agentGain).connect(destination);
            }
          };

          // Create data channel with explicit configuration
          const dataChannel = pc.createDataChannel("oai-events", {
            ordered: true,
          });
          setDataChannel(dataChannel);

          dataChannel.addEventListener("open", () => {
            configureData(dataChannel);
          });

          dataChannel.addEventListener("message", async (ev) => {
            const message = JSON.parse(ev?.data);
            console.log(message, "message");
            const responseType = message?.type;

            if (responseType === "session.created") {
              toggleMute();

              // Send initial greeting immediately for faster interaction
              const event = {
                event_id: generateUUID(),
                type: "conversation.item.create",
                previous_item_id: null,
                item: {
                  id: generateShortId(),
                  type: "message",
                  role: "user",
                  content: [
                    {
                      type: "input_text",
                      text: "Hi",
                    },
                  ],
                },
              };
              dataChannel.send(JSON.stringify(event));
              setIsLoading(false);
              setTimeout(() => {
                toggleMute(false);
              }, 5000);
            }

            // user conversation
            if (
              responseType ===
                "conversation.item.input_audio_transcription.completed" &&
              message?.transcript?.trim() !== "Hi"
            ) {
              setTranscribedParagraph(
                (prev) => `${prev}\nUser:\n${message.transcript}`
              );
              userTranscriptionRef.current = [
                ...userTranscriptionRef.current,
                {
                  data: message.transcript,
                  mode: "voice",
                },
              ];
              setMessages((prev) => [
                ...prev,
                {
                  type: "user",
                  text: message.transcript,
                  timestamp: Date.now(),
                },
              ]);
            }

            // if (responseType === "response.audio_transcript.delta") {
            //   currentIncrementalTextRef.current += message.delta;

            //   setMessages((prev) => {
            //     const newMessages = [...prev];
            //     if (
            //       newMessages.length > 0 &&
            //       newMessages[newMessages.length - 1].type === "agent"
            //     ) {
            //       // Update existing agent message
            //       newMessages[newMessages.length - 1].text =
            //         currentIncrementalTextRef.current;
            //     } else {
            //       // Create new agent message if none exists or last message was from user
            //       newMessages.push({
            //         type: "agent",
            //         text: currentIncrementalTextRef.current,
            //         timestamp: Date.now(),
            //       });
            //     }
            //     return newMessages;
            //   });
            // }

            // agent conversation
            if (responseType === "response.audio_transcript.done") {
              setTranscribedParagraph(
                (prev) => `${prev}\nAgent:\n${message.transcript}\n`
              );
              agentTranscriptionRef.current = [
                ...agentTranscriptionRef.current,
                {
                  data: message.transcript,
                  mode: "voice",
                },
              ];
              currentIncrementalTextRef.current = "";
              setMessages((prev) => {
                const newMessages = [...prev];
                // Check if the last message is from agent and update it, otherwise add new
                // if (
                //   newMessages.length > 0 &&
                //   newMessages[newMessages.length - 1].type === "agent"
                // ) {
                //   newMessages[newMessages.length - 1] = {
                //     ...newMessages[newMessages.length - 1],
                //     text: message.transcript,
                //     timestamp: Date.now(),
                //   };
                // } else {
                newMessages.push({
                  type: "agent",
                  text: message.transcript,
                  timestamp: Date.now(),
                });
                // }
                return newMessages;
              });
            }

            if (responseType === "conversation.item.created") {
              // Trigger agent response after user message is created
              dataChannel.send(JSON.stringify({ type: "response.create" }));
            }

            if (responseType === "response.function_call_arguments.done") {
              const { name, call_id } = message;
              const args = message?.arguments;

              let body: any = {};

              if (args) {
                try {
                  body = typeof args === "string" ? JSON.parse(args) : args;
                } catch (error) {
                  console.error("Failed to parse args:", error, args);
                }
              }

              console.log("body", body, message);
              if (name === "get_specialities") {
                // Call your function
                const result = await getSpecialities(body);
                console.log(result, "getSpecialities");
                // Send tool result back to OpenAI
                const event = {
                  type: "conversation.item.create",
                  item: {
                    type: "function_call_output",
                    call_id,
                    output: JSON.stringify(result),
                  },
                };
                dataChannel.send(JSON.stringify(event));
                const specialitiesData = result?.map((item: any) => ({
                  name: item.speciality_name,
                  key: "specialities",
                }));
                setOptions(specialitiesData);
                setSpecialitiesList(specialitiesData);
              }

              if (name === "get_doctors") {
                // Call your function
                const result = await getDoctors(body);
                console.log(result, "getDoctors");
                doctorsListRef.current = result;

                // Send tool result back to OpenAI
                const event = {
                  type: "conversation.item.create",
                  item: {
                    type: "function_call_output",
                    call_id,
                    output: JSON.stringify(result),
                  },
                };
                dataChannel.send(JSON.stringify(event));
                const doctorsData = result?.map((item: any) => ({
                  name: item?.um_name || item,
                  key: "doctors",
                }));
                setOptions(doctorsData);
                setDoctorsList(doctorsData);
              }

              if (name === "get_doctors_by_name") {
                // Call your function
                const result = await getDoctorsByName(body);
                console.log(result, "getDoctorsByName");
                doctorsListRef.current = result;

                // Send tool result back to OpenAI
                const event = {
                  type: "conversation.item.create",
                  item: {
                    type: "function_call_output",
                    call_id,
                    output: JSON.stringify(result),
                  },
                };
                dataChannel.send(JSON.stringify(event));
                setOptions(
                  result?.map((item: any) => ({
                    name: item.um_name,
                    key: "",
                  }))
                );
              }

              if (name === "get_appointment_slots") {
                setSelectedDoctor(body);
                if (body?.hm_id && body?.um_id && body?.hm_business_id) {
                  const result = await getAppointmentSlotsForVoice(body);
                  const recentSlots = result.slice(0, 2);
                  setOptions([
                    ...recentSlots.map((slot: any) => ({
                      name: slot.start,
                      key: "slots",
                    })),
                    { name: "List all available slots", key: "slots" },
                  ]);
                  const formattedResult = result.map((item: any) => ({
                    appointment_start_time: item.start,
                    appointment_end_time: item.end,
                    appointment_duration: item?.duration,
                  }));
                  console.log(result, "getAppointmentSlots");
                  // Send tool result back to OpenAI
                  const event = {
                    type: "conversation.item.create",
                    item: {
                      type: "function_call_output",
                      call_id,
                      output: JSON.stringify(
                        formattedResult?.length === 0
                          ? "No slots available"
                          : formattedResult.slice(0, 50)
                      ),
                    },
                  };
                  dataChannel.send(JSON.stringify(event));
                }
              }

              if (name === "get_patient_details") {
                // Call your function
                const result = await getPatientDetails(body);
                console.log(result, "getPatientDetails");
                patientsDetailsRef.current = result;

                // Send tool result back to OpenAI
                const event = {
                  type: "conversation.item.create",
                  item: {
                    type: "function_call_output",
                    call_id,
                    output: JSON.stringify(result),
                  },
                };
                dataChannel.send(JSON.stringify(event));
                const patientsData = result?.map((item: any) => ({
                  name: `${item.pm_first_name} (${
                    item.pm_gender === "Female" ? "F" : "M"
                  }, ${item.pm_dob ? calculateAge(item.pm_dob)?.years : ""})`,
                  key: "patient_list",
                }));
                setPatientList(patientsData);
                setOptions([
                  ...patientsData,
                  {
                    name: "new patient",
                    key: "patient_list",
                  },
                ]);
              }

              if (name === "add_patient") {
                // Call your function
                const result = await addNewPatient(body);
                console.log(result, "add_patient");
                // Send tool result back to OpenAI
                const event = {
                  type: "conversation.item.create",
                  item: {
                    type: "function_call_output",
                    call_id,
                    output: JSON.stringify(result),
                  },
                };
                dataChannel.send(JSON.stringify(event));
              }

              if (name === "book_appointment") {
                // Call your function
                setIsEnd(true);
                toggleMute(true);
                const selectedPatient = patientsDetailsRef?.current?.find(
                  (item: any) => item?.pm_pid === body?.pm_pid
                );
                const doctorDetails = doctorsListRef?.current?.find(
                  (item: any) => item?.um_id == body?.um_id
                );
                setSelectedDoctor(doctorDetails);

                setAppointmentDetails({
                  ...body,
                  ...selectedPatient,
                  call_id: call_id,
                  um_name: doctorDetails?.um_name,
                  hm_business_id: agentDetails?.hm_business_id,
                });
              }

              // if (name === "cancel_appointment") {
              //   // Call your function
              //   const result = await cancelAppointment(body);
              //   console.log(result, "cancelAppointment");

              //   // Send tool result back to OpenAI
              //   const event = {
              //     type: "conversation.item.create",
              //     item: {
              //       type: "function_call_output",
              //       call_id,
              //       output: JSON.stringify(result),
              //     },
              //   };
              //   dataChannel.send(JSON.stringify(event));
              // }

              if (name === "reschedule_appointment") {
                // Call your function
                console.log(body, "body");
                setIsEnd(true);
                toggleMute(true);
                const selectedPatient = patientsDetailsRef?.current?.find(
                  (item: any) => item?.pm_pid === body?.pm_pid
                );
                const doctorDetails = doctorsListRef?.current?.find(
                  (item: any) => item?.um_id == body?.um_id
                );

                setAppointmentDetails({
                  ...body,
                  ...selectedPatient,
                  call_id: call_id,
                  um_name: doctorDetails?.um_name,
                });
              }

              if (name === "appointment_slots_validator") {
                // Call your function
                const result = await appointmentSlotsValidator(body);
                console.log(result, "appointmentSlotsValidator");

                // Send tool result back to OpenAI
                const event = {
                  type: "conversation.item.create",
                  item: {
                    type: "function_call_output",
                    call_id,
                    output: JSON.stringify(result),
                  },
                };
                dataChannel.send(JSON.stringify(event));
              }

              if (name === "edit_patient") {
                // Call your function
                const result = await editPatient(body);
                console.log(result, "editPatient");

                // Send tool result back to OpenAI
                const event = {
                  type: "conversation.item.create",
                  item: {
                    type: "function_call_output",
                    call_id,
                    output: JSON.stringify(result),
                  },
                };
                dataChannel.send(JSON.stringify(event));
              }

              // Have assistant respond after getting the results
              dataChannel.send(JSON.stringify({ type: "response.create" }));
            }
          });

          // Create and configure MediaRecorder with explicit mime type check
          const merged = destination.stream;

          const mimeType = isIOS() ? "audio/mp4" : "audio/webm;codecs=opus";

          let recorder: MediaRecorder;
          if (MediaRecorder.isTypeSupported(mimeType)) {
            recorder = new MediaRecorder(merged, {
              mimeType,
              audioBitsPerSecond: 128000,
            });
          } else {
            // Fallback to default
            recorder = new MediaRecorder(merged);
          }

          recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              setConversationChunks((prev) => [...prev, event.data]);
            }
          };

          recorder.start(1000); // Record in 1-second chunks
          setMergedRecorder(recorder);

          // Create and set offer
          const offer = await pc.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: false,
          });
          await pc.setLocalDescription(offer);

          try {
            const selectedLang = languageOptions.find(
              (lang) => lang.value === selectedLanguage
            );
            const ephemeralKey = await startAzureOpenAISession(
              agentDetails,
              instructions,
              selectedLang?.languageCode || "en",
              agentDetails?.hospitalType
            );
            if (!ephemeralKey) {
              throw new Error(
                "Failed to create session - no ephemeral key received"
              );
            }

            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
              audioRef.current.src = ""; // Clear the source
              audioRef.current.remove(); // Remove the element
              audioRef.current = null;
            }

            const answer = await establishWebRtcConnection(ephemeralKey, offer);

            if (!answer) {
              throw new Error("No answer received from server");
            }

            try {
              await pc.setRemoteDescription({ sdp: answer, type: "answer" });
            } catch (sdpError) {
              console.error("SDP Error:", sdpError);
              throw new Error("Failed to set remote description");
            }

            setupAudioAnalysis(stream);
            setIsListening(true);
          } catch (connectionError) {
            console.error("Connection Error:", connectionError);
            throw connectionError;
          }
        } catch (processingError) {
          console.error("Audio processing error:", processingError);
          if (audioContextRef.current) {
            audioContextRef.current.close().catch(console.error);
          }
          throw new Error("Failed to setup audio processing");
        }
        peerConnectionRef.current = pc;
      } catch (error: unknown) {
        console.error("Detailed error:", error);

        const webRTCError = error as Error & WebRTCError;
        let errorMessage = "Failed to start recording. ";

        // More detailed error handling
        if (
          webRTCError.name === "NotAllowedError" ||
          webRTCError.message?.includes("permission")
        ) {
          errorMessage +=
            "Please allow microphone access in your browser settings and refresh the page.";
        } else if (
          webRTCError.name === "NotFoundError" ||
          webRTCError.message?.includes("microphone")
        ) {
          errorMessage +=
            "Could not access your microphone. Please make sure it's properly connected and not in use by another application.";
        } else if (webRTCError.name === "NotReadableError") {
          errorMessage +=
            "Could not access your microphone. It might be in use by another application.";
        } else if (webRTCError.message?.includes("session")) {
          errorMessage += "Failed to create session. Please try again.";
        } else if (webRTCError.message?.includes("SDP")) {
          errorMessage +=
            "Connection setup failed. Please refresh the page and try again.";
        } else {
          console.error("Unknown error:", webRTCError);
          errorMessage += `${
            webRTCError.message || "An unexpected error occurred"
          }. Please try again.`;
        }

        alert(errorMessage);

        // Cleanup on error
        if (peerConnection) {
          peerConnection.close();
          setPeerConnection(null);
        }
        if (mediaStream) {
          mediaStream.getTracks().forEach((track) => track.stop());
          setMediaStream(null);
        }
        setIsListening(true);
        setIsLoading(false);
      }
    }
  };

  const stopListening = async () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Close audio level detection context
    if (
      audioLevelContextRef.current &&
      audioLevelContextRef.current.state !== "closed"
    ) {
      try {
        await audioLevelContextRef.current.close();
      } catch (err) {
        console.error("Error closing audio level AudioContext:", err);
      }
      audioLevelContextRef.current = null;
    }

    // Close main audio context
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        await audioContextRef.current.close();
      } catch (err) {
        console.error("Error closing AudioContext:", err);
      }
      audioContextRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    if (dataChannel) dataChannel.close();
    if (peerConnection) peerConnection.close();
    if (mediaStream) mediaStream.getTracks().forEach((track) => track.stop());

    if (mergedRecorder && mergedRecorder.state !== "inactive") {
      mergedRecorder.stop();
    }

    try {
      peerConnectionRef.current = null;
      setIsListening(false);
      setPeerConnection(null);
      setMediaStream(null);
      setMergedRecorder(null);
    } catch (error) {
      console.error("Error in conversation handling:", error);
      alert("Failed to process conversation. Please try again.");
    }
    setDataChannel(null);
    setPeerConnection(null);
    setMediaStream(null);
    setMergedRecorder(null);
    setSoundLevel(1);
    setIsListening(false);

    if (socketRef.current && socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.close();
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
  };

  // Handle text submission with logging
  const handleSubmit = (
    value: any,
    key?: string,
    notPassData?: boolean,
    noShowMessage?: boolean
  ) => {
    if (typeof value === "string") {
      value = value?.trim();
    }
    if (value === "List all available slots") {
      setShowSelectSlot(true);
      return;
    }
    if (
      key === "slots" &&
      !value?.includes("I want to book an appointment for")
    ) {
      value = `I want to book an appointment for ${dayjs().format(
        "DD MMM YY"
      )} ${value}`;
    }
    if (value === "new patient") {
      trackEvent("AA_AddNewPatients", {
        ...moengageData,
        "Owner Doctor Name":
          selectedDoctor?.doctor_name || selectedDoctor?.doctors_name,
        "Owner Doctor Unique ID": selectedDoctor?.um_id,
      });
    }
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      setIsChatLoading(true);
      const message = {
        type: "text",
        text: value,
        language: selectedLanguage,
      };

      try {
        if (!notPassData) {
          socketRef.current.send(JSON.stringify(message));
        }

        if (!noShowMessage) {
          setMessages((prev: any) => [
            ...prev,
            {
              type: "user",
              text: value,
              timestamp: Date.now(),
              key: key,
            },
          ]);
          userTranscriptionRef.current = [
            ...userTranscriptionRef.current,
            { data: value, mode: "text" },
          ];
          setTranscribedParagraph((prev) => `${prev}\nUser:\n${value}`);

          setValue("");
          setOptions([]);
          setShowGender(false);
          setShowAge(false);
        }
      } catch (error) {
        console.error("Error sending text message:", error);
      }
    }
  };

  // Generate a shorter ID (32 characters or less)
  const generateShortId = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  const handleSelectedOptions = (value: any) => {
    if (value === "List all available slots") {
      setShowSelectSlot(true);
      return;
    }
    if (isVoiceSelected) {
      // Voice mode - use WebRTC data channel
      if (!dataChannel || dataChannel.readyState !== "open") {
        console.warn("Data channel not ready for voice mode");
        return;
      }

      const event = {
        event_id: generateUUID(),
        type: "conversation.item.create",
        previous_item_id: null,
        item: {
          id: generateShortId(),
          type: "message",
          role: "user",
          content: [
            {
              type: "input_text",
              text: value,
            },
          ],
        },
      };

      try {
        dataChannel.send(JSON.stringify(event));
        setMessages((prev) => [
          ...prev,
          {
            type: "user",
            text: value,
            timestamp: Date.now(),
          },
        ]);
        userTranscriptionRef.current = [
          ...userTranscriptionRef.current,
          {
            data: value,
            mode: "text",
          },
        ];
        setTranscribedParagraph((prev) => `${prev}\nUser:\n${value}`);
        setOptions([]);
        dataChannel.send(
          JSON.stringify({
            type: "output_audio_buffer.clear",
          })
        );
      } catch (error) {
        console.error("Failed to send message via data channel:", error);
      }
    }
  };

  const scrollToBottom = () => {
    // Use scrollIntoView for the last message element - more reliable than scrolling containers
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const cleanup = async () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        setIsListening(false);
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
    }

    stopRecording();
    lastTranscriptRef.current = "";

    // Clean up audio contexts safely
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        await audioContextRef.current.close();
      } catch (err) {
        console.error("Error closing AudioContext in cleanup:", err);
      }
      audioContextRef.current = null;
    }

    if (
      audioLevelContextRef.current &&
      audioLevelContextRef.current.state !== "closed"
    ) {
      try {
        await audioLevelContextRef.current.close();
      } catch (err) {
        console.error(
          "Error closing audio level AudioContext in cleanup:",
          err
        );
      }
      audioLevelContextRef.current = null;
    }
  };

  const handleExitPopup = () => {
    setShowExitPopup((prev) => !prev);
  };

  // Update the Yes, Submit to Doctor button click handler
  const handleSubmitToDoctor = async () => {
    setIsSubmitLoading(true);
    try {
      let result: any;
      if (appointmentDetails?.pam_id) {
        result = await rescheduleAppointment(appointmentDetails);
      } else {
        result = await bookAppointment(appointmentDetails);
      }
      const doctorDetails = doctorsListRef?.current?.find(
        (item: any) => item?.um_id === result?.um_id
      );
      if (appointmentDetails?.pam_id) {
        trackEvent("AA_Reschedule_Apt", {
          ...moengageData,
          "Owner Doctor Name": doctorDetails?.um_name,
          "Owner Doctor Specialty": doctorDetails?.speciality,
          "Owner Doctor Unique ID": result?.um_id,
          "Patient Name": appointmentDetails?.pm_first_name,
          "Patient Id": result?.pam_pid,
          "Patient Age": calculateAge(appointmentDetails?.pm_dob)?.years,
          "Patient Gender": appointmentDetails?.pm_gender,
          "Appointment Time": result?.appointment_date || result?.date,
          "No of Interactions": messages?.length,
          "Type of Interaction": type,
          "Appointment ID": result?.pam_id,
        });
      } else {
        trackEvent("AA_Confirm_Slot", {
          ...moengageData,
          "Owner Doctor Name": doctorDetails?.um_name,
          "Owner Doctor Specialty": doctorDetails?.speciality,
          "Owner Doctor Unique ID": result?.um_id,
          "Patient Name": appointmentDetails?.pm_first_name,
          "Patient Id": result?.pam_pid,
          "Patient Age": calculateAge(appointmentDetails?.pm_dob)?.years,
          "Patient Gender": appointmentDetails?.pm_gender,
          "Appointment Time": result?.appointment_date || result?.date,
          "No of Interactions": messages?.length,
          "Type of Interaction": type,
          "Appointment ID": result?.pam_id,
        });
      }
      if (result?.status === 200 || result?.status === 201) {
        const response = await saveConversationData(
          userTranscriptionRef.current,
          agentTranscriptionRef.current,
          conversationId,
          conversationChunks,
          transcribedParagraph,
          selectedLanguage,
          appointmentDetails.um_id,
          appointmentDetails.patient_unique_id,
          appointmentDetails.hm_id,
          result.pam_id,
          appointmentDetails.hm_business_id,
          appointmentDetails.pm_pid,
          type
        );
        navigate("/success-appointment", {
          state: {
            ...state,
            ...result,
            ...appointmentDetails,
            agentDetails,
            doctor_name: doctorDetails?.um_name,
            doctor_speciality: doctorDetails?.speciality,
            um_id: appointmentDetails.um_id,
            pm_pid: result?.pm_pid,
            messages: messages,
            type: type,
            avatarImage: avatarImage,
            doctosList: doctosList,
            patientList: patientList,
            specialitiesList: specialitiesList,
            patientsDetailsRef: patientsDetailsRef.current,
            doctorsListRef: doctorsListRef.current,
            conversationId: response?._id,
          },
        });
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
        }
        cleanup();
        stopListening();
        audioBlobsRef.current = [];
        setConversationChunks([]);
        userTranscriptionRef.current = [];
        agentTranscriptionRef.current = [];
        if (result?.message) {
          handleSubmit(result?.message, undefined, undefined, true);
          setIsEnd(false);
        }
      } else {
        if (isVoiceSelected) {
          toggleMute(false);
          setIsEnd(false);
          setOptions([]);
          const event = {
            type: "conversation.item.create",
            item: {
              type: "function_call_output",
              call_id: appointmentDetails?.call_id,
              output: JSON.stringify(result),
            },
          };
          dataChannel?.send(JSON.stringify(event));
          dataChannel?.send(JSON.stringify({ type: "response.create" }));
        } else {
          handleSubmit(result?.message, undefined, undefined, true);
          setIsEnd(false);
          setOptions([]);
        }
      }
    } catch (error) {
      console.error("Error submitting conversation:", error);
    }
    setIsSubmitLoading(false);
  };

  // Define toggleMute function with forceState parameter
  const toggleMute = useCallback(
    (forceState?: boolean) => {
      const currentStream = streamRef.current || mediaStream;

      if (currentStream) {
        const audioTracks = currentStream.getAudioTracks();

        // If forceState is provided, use it; otherwise toggle current state
        const newMutedState = forceState !== undefined ? forceState : !isMuted;

        audioTracks.forEach((track) => {
          // enabled should be the opposite of muted
          track.enabled = !newMutedState;
        });

        setIsMuted(newMutedState);
      }
    },
    [mediaStream, isMuted]
  );

  const renderMessages = useMemo(() => {
    return (
      <>
        {messages.map((message: any, index: number) => (
          <motion.div
            key={index}
            initial={{
              opacity: 0,
              scale: 0.5,
              transformOrigin:
                message.type === "agent" ? "top left" : "top right",
            }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.2 }}
            className={`flex ${
              message.type === "agent" ? "items-start" : "items-end gap-3"
            } mb-4 w-[100%]`}
          >
            {message.type === "agent" && (
              <div className="relative w-[32px] h-8 bg-gradient-to-br from-[rgba(150,72,254,0.4)] to-[rgba(242,77,182,1)] rounded-tl-[10px] rounded-tr-[10px] rounded-bl-[10px] flex items-center justify-center overflow-hidden mt-3.5">
                <img
                  src={avatarImage}
                  alt="avatar"
                  className="w-12 h-10 object-cover -mb-2.5"
                />
              </div>
            )}
            <div
              className={`flex gap-3 items-center
                     ${message.type === "agent" ? "mr-0 w-[88%]" : "ml-auto"}`}
            >
              {message?.key && (
                <Edit
                  className="w-6 h-6 min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px] inline-flex items-center justify-center text-[#4B4AD5]"
                  onClick={() => {
                    setIsEnd(false);
                    if (message?.key === "slots") {
                      setShowSelectSlot(true);
                      setOptions([
                        {
                          name: "List all available slots",
                          key: "slots",
                        },
                      ]);
                    } else {
                      setShowSelectSpecialityDrawer(true);
                      if (message?.key === "specialities") {
                        setOptions(specialitiesList);
                      } else if (message?.key === "doctors") {
                        setOptions(doctosList);
                      } else if (message?.key === "patient_list") {
                        setOptions([
                          ...patientList,
                          {
                            name: "new patient",
                            key: "patient_list",
                          },
                        ]);
                      }
                    }
                  }}
                />
              )}
              <div
                className={`p-3.5 flex items-center gap-3 text-2xl ${
                  message.type === "agent"
                    ? "rounded-tl-[18px] rounded-r-[18px]"
                    : "shadow-[0px_0px_12px_0px_#A238FF0D] rounded-tr-[18px] rounded-l-[18px] bg-white"
                }`}
                style={{ lineHeight: "42px" }}
              >
                {message.type === "agent" &&
                index >= initialMessageCountRef.current &&
                isVoiceSelected ? (
                  <AnimatedMessage
                    key={`${message.text}-${index}`}
                    text={message.text}
                    speed={80}
                    isLoading={isLoading}
                    className="text-sm leading-5 text-gray-800 font-normal"
                  />
                ) : (
                  <p className="text-sm leading-5 text-gray-800 font-normal">
                    {message.text}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </>
    );
  }, [messages, isLoading, isVoiceSelected]);

  const renderVoiceMessages = useMemo(() => {
    if (
      (selectedLanguage === "gujarati" &&
        messages?.[messages?.length - 1]?.type !== "user") ||
      selectedLanguage !== "gujarati"
    ) {
      return (
        <div
          className="h-[600px] overflow-y-auto text-2xl"
          ref={messageContainerRef}
          style={{ opacity: "0.6", lineHeight: "42px" }}
        >
          {messages.length - 1 >= initialMessageCountRef.current ? (
            <AnimatedMessage
              key={messages?.[messages?.length - 1]?.text || "default"}
              text={messages?.[messages?.length - 1]?.text || ""}
              speed={150}
            />
          ) : (
            <p className="text-sm leading-5 text-gray-800 font-normal">
              {messages?.[messages?.length - 1]?.text || ""}
            </p>
          )}
        </div>
      );
    }
  }, [messages, selectedLanguage]);

  return (
    <Layout>
      <div
        className="flex flex-col relative overflow-hidden"
        style={{
          height: "100dvh",
        }}
      >
        {/* Background Image */}
        <div
          className={`fixed inset-0 ${
            isVoiceSelected ? "top-[66px]" : "top-[58px]"
          }`}
        >
          <img
            src={DDxInputSelectorBg}
            alt="background"
            className="h-full w-full object-cover opacity-5"
          />
        </div>
        {/* Header */}
        <div>
          <div
            className="flex items-center justify-between gap-x-3 p-[17px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(250, 245, 253, 1))",
            }}
          >
            <div className="flex items-center gap-x-2">
              {isVoiceSelected ? (
                <div className="relative w-[32px] h-8 bg-gradient-to-br from-[rgba(150,72,254,0.4)] to-[rgba(242,77,182,1)] rounded-[10px] flex items-center justify-center overflow-hidden">
                  <img
                    className="absolute top-[57%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-[60px] z-10 animate-slideUpFromBubble"
                    src={avatarImage}
                    alt="client"
                  />
                </div>
              ) : (
                <img
                  src={close}
                  alt={`${selectedLanguage}-strip`}
                  className="w-[24px] h-[24px] rounded-full object-cover cursor-pointer"
                  onClick={handleExitPopup}
                />
              )}
              <span className="font-semibold text-base">
                {LangSpecificMsgData.bookAppointment}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative px-4 overflow-hidden">
          <div
            className={`relative z-10 gap-3 pt-4 overflow-y-auto no-scrollbar h-full ${
              isEnd ? "pb-[240px]" : "pb-[130px]"
            }`}
          >
            {isLoading && isVoiceSelected ? (
              <div className="flex flex-col items-center justify-center h-full">
                <img
                  src={speakLoading}
                  alt="success"
                  className={`transition-all duration-2000 ease-in-out`}
                  width={200}
                  height={120}
                />
                <div className="relative overflow-hidden">
                  <span className="text-lg text-[#454551] font-medium text-center relative">
                    Connecting with the Agent!
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
                  </span>
                </div>
              </div>
            ) : null}
            {isListening && isVoiceSelected && !isLoading ? (
              <div className="flex flex-col overflow-x-hidden max-h-[380px]">
                {/*
                <div className="sticky flex justify-center items-center h-[300px] w-full">
                   <AnimatePresence>
                    {messages[messages.length - 1]?.type !== "agent" &&
                      !isLoading && (
                        <motion.div
                          initial={{ width: 0, height: 0, opacity: 0 }}
                          animate={{ width: 350, height: 350, opacity: 1 }}
                          exit={{ width: 0, height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                        >
                          <Lottie
                            animationData={voiceWave}
                            autoplay
                            loop
                            className="w-full h-full"
                          />
                        </motion.div>
                      )}
                  </AnimatePresence>

                   <motion.div
                    animate={{
                      rotateZ: isAudioDetected && !isMuted 
                        ? [0, audioIntensity * 2.5, -audioIntensity * 2.5, audioIntensity * 1.2, 0]
                        : 0,
                      scale: 1 + audioIntensity * 0.04, // Max 40% scale increase (0-10 range)
                      y: isAudioDetected && !isMuted 
                        ? [0, -audioIntensity * 2.5, 0] // 25px vertical bounce (0-10 range)
                        : 0,
                    }}
                    transition={{
                      duration: 0.3, // Faster animation for more dynamic effect
                      ease: [0.34, 1.56, 0.64, 1], // Smooth spring-like easing
                    }}
                    className={`relative flex justify-center items-center ${isLoading ? "w-[150px] h-[150px]" : messages[messages.length - 1]?.type === "user" ? "w-[180px] h-[180px]" : "w-[200px] h-[200px]"}`}
                  >
                    <motion.div
                      animate={{
                        filter: isLoading 
                          ? "grayscale(100%) brightness(0.8)" 
                          : `brightness(${1 + audioIntensity * 0.08})`, // Increased brightness effect
                      }}
                      transition={{
                        duration: 0.2, // Faster brightness changes
                        ease: "easeOut",
                      }}
                    >
                      <Lottie
                        animationData={aiBuble}
                        autoplay
                        loop
                        style={{ width: '100%', height: '100%' }}
                      />
                    </motion.div>
                    
                    <motion.div
                      className="absolute inset-0 rounded-full blur-3xl -z-10"
                      animate={{
                        opacity: audioIntensity * 0.08, // Max 0.8 opacity (0-10 range) - doubled
                        scale: 1 + audioIntensity * 0.08, // Max 0.8 scale increase (0-10 range) - doubled
                      }}
                      transition={{
                        duration: 0.2, // Faster glow changes
                        ease: "easeOut",
                      }}
                    />
                    {messages[messages.length - 1]?.type === "agent" &&
                      !isLoading && (
                        <img
                          className="absolute top-[57%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[45%] rounded-[60px] z-10 animate-slideUpFromBubble"
                          src={avatarImage}
                          alt="avatar"
                        />
                      )}
                  </motion.div> 
                </div> */}
                {renderVoiceMessages}
              </div>
            ) : (
              <div
                className="flex-1 overflow-y-auto"
                ref={textMessagesContainerRef}
              >
                <AnimatePresence mode="wait">
                  {renderMessages}
                  {showGender && (
                    <GenderInput
                      gender={gender}
                      setGender={setGender}
                      disabled={false}
                      handleSubmit={(selectedGender: any) => {
                        handleSubmit(selectedGender, "", true);
                        setShowGender(false);
                        setShowAge(true);
                      }}
                      onPressEdit={() => {
                        setGender(null);
                        setShowGender(true);
                      }}
                      data={[
                        { label: "Male", value: "Male" },
                        { label: "Female", value: "Female" },
                      ]}
                      title={"Select Patient's Gender"}
                    />
                  )}
                  {showAge && (
                    <AgeInput
                      age={age}
                      setAge={setAge}
                      disabled={false}
                      onPressEdit={() => setAge(null)}
                      handleSubmit={(selectedAge: any) => {
                        handleSubmit(selectedAge, "", true);

                        // Calculate date of birth from age
                        let dateOfBirth = selectedAge;
                        if (selectedAge?.includes("years")) {
                          const ageMatch = selectedAge.match(/(\d+)/);
                          if (ageMatch) {
                            const ageYears = parseInt(ageMatch[1]);
                            const today = dayjs();
                            const birthDate = today.subtract(ageYears, "year");
                            dateOfBirth = birthDate.format("YYYY-MM-DD");
                          }
                        }

                        const message = {
                          type: "text",
                          text: `
                        ${
                          userTranscriptionRef.current[
                            userTranscriptionRef?.current?.length - 4
                          ]?.data
                        }, ${gender}, ${dateOfBirth}`,
                          language: selectedLanguage,
                        };
                        socketRef?.current?.send(JSON.stringify(message));
                        setShowAge(false);
                      }}
                    />
                  )}
                  {showSkeletonLoaderData && (
                    <div className="flex items-start mb-4 w-full gap-3">
                      <div className="relative w-[32px] h-8 bg-gradient-to-br from-[rgba(150,72,254,0.4)] to-[rgba(242,77,182,1)] rounded-tl-[10px] rounded-tr-[10px] rounded-bl-[10px] flex items-center justify-center overflow-hidden mt-3.5">
                        <img
                          src={avatarImage}
                          alt="avatar"
                          className="w-12 h-10 object-cover -mb-2.5"
                        />
                      </div>
                      <div className="flex gap-3 items-center mr-0 w-[88%]">
                        <img
                          src={chatTypingIdicator}
                          className="w-12 h-12 mt-2"
                          alt="chat-typing-idicator"
                        />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                  {/* Scroll target for latest message */}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        {/* Footer (kept fixed at bottom via flex-col) */}
        {isVoiceSelected ? (
          <>
            <div className="fixed-bottom-controls flex flex-col justify-between gap-2 w-full">
              <div className="flex items-center w-full max-w-[450px] mx-auto gap-4">
                {!isLoading && (
                  <button
                    onClick={() => {
                      handleExitPopup();
                    }}
                    className="control-button"
                    style={{ border: "none", flexShrink: 0 }}
                  >
                    <img src={chat} alt="chat" className="w-[70px] h-[70px]" />
                  </button>
                )}
                <div className="flex-1 flex flex-col items-center gap-2 min-w-0">
                  {/* Audio level indicator */}
                  {isListening && !isLoading && !isMuted && (
                    <div
                      className="flex justify-center items-center w-full"
                      style={{
                        maxWidth: "calc(100% - 160px)",
                        minWidth: 0,
                      }}
                    >
                      <VoiceWaveVisualizer
                        isRecording={isListening}
                        isPaused={isMuted}
                        width="100%"
                        height={60}
                      />
                    </div>
                  )}
                </div>
                {!isLoading && (
                  <button
                    onClick={() => toggleMute()}
                    className="control-button"
                    style={{ border: "none", flexShrink: 0 }}
                  >
                    <img
                      src={isMuted ? muteMic : activeMic}
                      alt={isMuted ? "Unmute microphone" : "Mute microphone"}
                      className="w-[70px] h-[70px]"
                    />
                  </button>
                )}
              </div>

              <div>
                <span className="text-base text-white">
                  {isLoading
                    ? ""
                    : isListening && !isMuted
                    ? messages?.[messages?.length - 1]?.type === "agent"
                      ? LangSpecificMsgData.agentSpeaking + "..."
                      : LangSpecificMsgData.listening + "..."
                    : "Muted"}
                </span>
              </div>
            </div>

            {options?.length > 0 && (
              <div
                className={`fixed bottom-20 w-full max-w-[450px] mx-auto z-20 ${
                  isVoiceSelected ? "bottom-32" : ""
                }`}
              >
                <div className="flex overflow-auto gap-2 items-center m-4 mb-3 no-scrollbar">
                  {options?.map((item: any, index: number) => {
                    if (index <= 3) {
                      return (
                        <div
                          key={index}
                          className="flex-grow-0 flex-shrink-0 cursor-pointer relative backdrop-blur-3xl"
                          style={{
                            background: "#564DD50F",
                            borderRadius: "20px",
                            padding: "8px 16px",
                            zIndex: 1,
                            minWidth: "fit-content",
                            maxWidth: "100%",
                            cursor: "pointer",
                            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                          }}
                          onClick={() => {
                            handleSelectedOptions(item?.name);
                          }}
                        >
                          <div
                            style={{
                              content: '""',
                              position: "absolute",
                              top: 0,
                              right: 0,
                              bottom: 0,
                              left: 0,
                              borderRadius: "20px",
                              padding: "1px",
                              background:
                                "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                              WebkitMask:
                                "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                              WebkitMaskComposite: "xor",
                              maskComposite: "exclude",
                              zIndex: -1,
                              pointerEvents: "none",
                            }}
                          />
                          <span
                            style={{
                              background:
                                "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                              WebkitBackgroundClip: "text",
                              backgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              color: "transparent",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              pointerEvents: "none",
                              paddingRight:
                                item?.name === "List all available slots"
                                  ? 10
                                  : "",
                            }}
                            className="font-medium"
                          >
                            {item?.name}
                          </span>
                          {item?.name === "List all available slots" && (
                            <img
                              src={chevron}
                              alt="chevron"
                              className="w-8 h-8 rotate-90 absolute right-0 bottom-1"
                            />
                          )}
                        </div>
                      );
                    }
                  })}
                  {options?.length > 3 && (
                    <div
                      className="flex-grow-0 flex-shrink-0 cursor-pointer relative backdrop-blur-xl"
                      style={{
                        background: "rgba(255, 255, 255, 0.5)",
                        borderRadius: "20px",
                        padding: "8px 16px",
                        zIndex: 1,
                        minWidth: "fit-content",
                        maxWidth: "100%",
                        cursor: "pointer",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0)",
                        color: "#7756D6",
                      }}
                      onClick={() => setShowSelectSpecialityDrawer(true)}
                    >
                      <div
                        style={{
                          content: '""',
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0,
                          borderRadius: "20px",
                          padding: "1px",
                          background:
                            "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                          WebkitMask:
                            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                          WebkitMaskComposite: "xor",
                          maskComposite: "exclude",
                          zIndex: -1,
                          pointerEvents: "none",
                        }}
                      />
                      <span
                        style={{
                          background:
                            "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          color: "transparent",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          pointerEvents: "none",
                          paddingRight: 10,
                        }}
                      >
                        View more
                      </span>
                      <img
                        src={chevron}
                        alt="chevron"
                        className="w-8 h-8 rotate-90 absolute right-0 bottom-1"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            className={`bottom-0 w-full max-w-[450px] mx-auto z-20 ${
              isSafari || isIOSDevice ? "fixed" : ""
            }`}
          >
            {options?.length > 0 && (
              <div className="flex overflow-auto gap-2 items-center m-4 mb-3 no-scrollbar">
                {options?.map((item: any, index: number) => {
                  if (index <= 3) {
                    return (
                      <div
                        key={index}
                        className="flex-grow-0 flex-shrink-0 cursor-pointer relative backdrop-blur-xl"
                        style={{
                          background: "rgba(255, 255, 255, 0.5)",
                          borderRadius: "20px",
                          padding: "8px 16px",
                          zIndex: 1,
                          minWidth: "fit-content",
                          maxWidth: "100%",
                          cursor: "pointer",
                          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0)",
                        }}
                        onClick={() => {
                          handleSubmit(item?.name, item?.key);
                        }}
                      >
                        <div
                          style={{
                            content: '""',
                            position: "absolute",
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            borderRadius: "20px",
                            padding: "1px",
                            background:
                              "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                            WebkitMask:
                              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                            WebkitMaskComposite: "xor",
                            maskComposite: "exclude",
                            zIndex: -1,
                            pointerEvents: "none",
                          }}
                        />
                        <span
                          style={{
                            background:
                              "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            color: "transparent",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            pointerEvents: "none",
                            paddingRight:
                              item?.name === "List all available slots"
                                ? 10
                                : "",
                          }}
                        >
                          {item?.name}
                        </span>
                        {item?.name === "List all available slots" && (
                          <img
                            src={chevron}
                            alt="chevron"
                            className="w-8 h-8 rotate-90 absolute right-0 bottom-1 ml-4"
                          />
                        )}
                      </div>
                    );
                  }
                })}
                {options?.length > 3 && (
                  <div
                    className="flex flex-row flex-grow-0 flex-shrink-0 cursor-pointer relative backdrop-blur-xl"
                    style={{
                      background: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "20px",
                      padding: "8px 16px",
                      zIndex: 1,
                      minWidth: "fit-content",
                      maxWidth: "100%",
                      cursor: "pointer",
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0)",
                      color: "#7756D6",
                    }}
                    onClick={() => setShowSelectSpecialityDrawer(true)}
                  >
                    <div
                      style={{
                        content: '""',
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        borderRadius: "20px",
                        padding: "1px",
                        background:
                          "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                        WebkitMask:
                          "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                        WebkitMaskComposite: "xor",
                        maskComposite: "exclude",
                        zIndex: -1,
                        pointerEvents: "none",
                      }}
                    />
                    <span
                      style={{
                        background:
                          "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        color: "transparent",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        pointerEvents: "none",
                        paddingRight: 10,
                      }}
                    >
                      View more
                    </span>
                    <img
                      src={chevron}
                      alt="chevron"
                      className="w-8 h-8 rotate-90 absolute right-0 bottom-1"
                    />
                  </div>
                )}
              </div>
            )}
            <div
              className="p-4 flex flex-col gap-8 bg-white border-t-[0.5px] border-t-[#E2E2EA] [border-radius:25px_25px_0_0]"
              style={{
                boxShadow: "0 -8px 10px rgba(255, 0, 191, 0.08)",
              }}
            >
              <div className="p-[1px] rounded-2xl bg-gradient-to-r from-[#A461D8] via-[#7656D6] to-[#554DD5]">
                <div className="rounded-2xl flex flex-col overflow-hidden bg-white">
                  <div className="relative rounded-2xl flex flex-col gap-1 py-1 px-4">
                    <textarea
                      id="symptoms-input"
                      name="symptoms"
                      className="w-full border-none outline-none bg-transparent resize-none text-[#454551] text-sm font-normal placeholder-[#98A2B3] caret-[#4B4AD5] py-2"
                      rows={1}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (value?.trim()) {
                            handleSubmit(value);
                          }
                        }
                      }}
                      onFocus={() => setIsTextButtonClicked(true)}
                      onBlur={() => setIsTextButtonClicked(false)}
                      placeholder="Type your message here"
                    />
                    <div className="absolute bottom-1.5 right-1.5 flex flex-row items-center justify-between">
                      <div />
                      {value?.length > 0 ? (
                        <div className="flex flex-row items-center gap-4">
                          <button onClick={() => handleSubmit(value)}>
                            <img src={bubble} alt="bubble" />
                          </button>
                        </div>
                      ) : // !isChatLoading ? (
                      //   <button onClick={() => setIsVoiceSelected(true)}>
                      //     <img src={voice} alt="voice" />
                      //   </button>
                      // ) :
                      null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isVoiceSelected && !isLoading && (
          <div className="fixed left-0 right-0 bottom-0 w-full max-w-[420px] mx-auto">
            <VoiceBubble />
            {/* <img src={voiceBottomBg} alt="voice bottom bg" className="w-full" /> */}
          </div>
        )}

        {/* Drawers */}
        {showSelectSpecialityDrawer && (
          <SelectSpecialityDrawer
            closeDrawer={() => setShowSelectSpecialityDrawer(false)}
            specialities={options}
            handleSelectedOptions={
              isVoiceSelected ? handleSelectedOptions : handleSubmit
            }
          />
        )}
        {showSelectSlot && (
          <SelectSlotDrawer
            closeDrawer={() => setShowSelectSlot(false)}
            handleSelectedOptions={
              isVoiceSelected ? handleSelectedOptions : handleSubmit
            }
            selectedDoctor={selectedDoctor}
            agentDetails={agentDetails}
          />
        )}
      </div>
      {showExitPopup && (
        <ConfirmationPopup
          show={showExitPopup}
          closePopup={handleExitPopup}
          handleConfirmation={() => {
            handleExitPopup();
            trackEvent("AA_Drop_Conversation", moengageData);
            navigate(-1);
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }}
        />
      )}

      {isEnd && (
        <ConfirmSlotDrawer
          LangSpecificMsgData={LangSpecificMsgData}
          appointmentDetails={appointmentDetails}
          patientGender={patientGender}
          patientAge={patientAge}
          handleSubmitToDoctor={handleSubmitToDoctor}
          isSubmitLoading={isSubmitLoading}
          continueBooking={() => {
            setIsEnd(false);
            toggleMute(false);
          }}
        />
      )}
    </Layout>
  );
};

export default React.memo(BookAppointment);